import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { DateTimeAspect } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { stateful, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { endOfDay, format, startOfDay } from 'date-fns';
import { html } from 'lit';
import { guard } from 'lit/directives/guard.js';
import { ContactEntity, ContactStatus } from '../../entities/contact.js';
import { ContactDetailView } from './ContactDetailView.js';

export const ContactsDashboard = stateful(() => {
  // Use useState for the selected contact and filters
  const [statusFilter, setStatusFilter] = useState<ContactStatus | 'all'>('all');
  const [selectedContactID, setSelectedContactID] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [companyFilter, setCompanyFilter] = useState<string>('');
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [checkedByHuman, setCheckedByHuman] = useState<'all' | 'yes' | 'no'>('all');

  // Function to handle row click
  const handleRowClick = (contact: ContactEntity) => {
    setSelectedContactID(contact._id);
  };

  // Function to close the detail view
  const closeDetailView = () => {
    setSelectedContactID(null);
  };

  // Function to clear all filters
  const clearFilters = () => {
    setStatusFilter('all');
    setSearchText('');
    setCompanyFilter('');
    setLocationFilter('');
    setStartDate('');
    setEndDate('');
    setCheckedByHuman('all');
  };

  const contactsDataviewOptions = useMemo(() => {
    const filterAspects: any[] = [];

    // Add status filter
    if (statusFilter !== 'all') {
      filterAspects.push(
        new StringAspect({
          fieldName: 'status',
          value: statusFilter,
          mode: 'equals',
        }),
      );
    }

    // Add company filter
    if (companyFilter.trim()) {
      filterAspects.push(
        new StringAspect({
          fieldName: 'companyName',
          value: companyFilter,
          mode: 'contains',
        }),
      );
    }

    // Add location filter
    if (locationFilter.trim()) {
      filterAspects.push(
        new StringAspect({
          fieldName: 'location',
          value: locationFilter,
          mode: 'contains',
        }),
      );
    }

    // Add date range filter - if both dates are provided, use range mode
    if (startDate && endDate) {
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'timestamp',
          firstValue: startOfDay(new Date(startDate)),
          secondValue: endOfDay(new Date(endDate)),
          mode: 'range',
        }),
      );
    } else if (startDate) {
      // Only start date - use after mode
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'timestamp',
          firstValue: startOfDay(new Date(startDate)),
          mode: 'after',
        }),
      );
    } else if (endDate) {
      // Only end date - use before mode
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'timestamp',
          secondValue: endOfDay(new Date(endDate)),
          mode: 'before',
        }),
      );
    }

    // Add checked by human filter
    if (checkedByHuman === 'yes') {
      filterAspects.push(
        new BooleanAspect({
          fieldName: 'isCheckedBySupport',
          value: true,
        }),
      );
    } else if (checkedByHuman === 'no') {
      // For "not checked", we want to include both false and null values
      // Use StringAspect with empty mode to catch all non-true values (null, false, undefined)
      filterAspects.push(
        new StringAspect({
          fieldName: 'isCheckedBySupport',
          mode: 'empty',
        }),
      );
    }

    return [
      buildFullOptions<typeof ContactEntity>({
        class: ContactEntity,
        customColumns: [
          {
            fieldName: 'timestamp',
            columnName: 'Timestamp',
            columnWidth: '175px',
            renderItem: value => format(value.timestamp ?? new Date(), 'Pp'),
          },
          {
            fieldName: 'companyName',
            columnName: 'Company Name',
            // columnWidth: '200px',
          },
          {
            fieldName: 'odooTicketID',
            columnName: 'Odoo Ticket ID',
            columnWidth: '150px',
          },
          {
            fieldName: 'email',
            columnName: 'Email',
            columnWidth: '200px',
            renderItem: value => html`
              ${value.email
                ? html`<a href="mailto:${value.email}" class="link link-primary">${value.email}</a>`
                : 'N/A'}
            `,
          },
          {
            fieldName: 'phone',
            columnName: 'Phone',
            columnWidth: '150px',
            renderItem: value => html`
              ${value.phone ? html`<a href="tel:${value.phone}" class="link link-primary">${value.phone}</a>` : 'N/A'}
            `,
          },
          {
            fieldName: 'position',
            columnName: 'Position',
            columnWidth: '150px',
          },
          {
            fieldName: 'location',
            columnName: 'Location',
            columnWidth: '100px',
          },
          {
            fieldName: 'isCheckedBySupport',
            columnName: 'Checked by Support',
            columnWidth: '100px',
            renderItem: value =>
              value.isCheckedBySupport
                ? html`<span class="text-secondary">✅</span>`
                : html`<span class="text-error">❌</span>`,
          },
          {
            fieldName: 'internalContact',
            columnName: 'Internal User',
            columnWidth: '100px',
            renderItem: value =>
              value.internalContact
                ? html`<span class="text-info">✅</span>`
                : html`<span class="text-base-content/30">❌</span>`,
          },
          {
            fieldName: 'status',
            columnName: 'Status',
            columnWidth: '120px',
            renderItem: value => html`
              <span
                class="badge ${value.status === ContactStatus.ACTIVE || value.status === ContactStatus.CUSTOMER
                  ? 'badge-success'
                  : value.status === ContactStatus.LEAD || value.status === ContactStatus.PROSPECT
                  ? 'badge-warning'
                  : 'badge-neutral'}"
                >${value.status || 'unknown'}</span
              >
            `,
          },
          {
            customID: '_options',
            columnName: '',
            columnWidth: '50px',
            renderItem: value => html`
              <button
                class="btn btn-ghost btn-sm btn-circle"
                @click=${() => handleRowClick(value)}
                title="View Details"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-5 h-5"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                  />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            `,
          },
        ],
        isSortable: false,
        emptyState: () => html`
          <div class="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div class="bg-base-200 p-4 rounded-full mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-12 h-12 text-secondary"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
                />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-secondary mb-2">No Contacts</h3>
            <p class="text-base-content opacity-70 max-w-md">
              There are currently no contacts to display. New contacts will appear here when they are added to the
              system.
            </p>
          </div>
        `,
        view: new View({
          filter: new AdornisFilter({
            searchFields: [
              'companyName',
              'email',
              'phone',
              'position',
              'location',
              'companyAction',
              'companyUpdates',
              'personAction',
              'personUpdates',
              'odooTicketID',
            ],
            search: searchText,
            aspects: filterAspects,
          }),
        }),
      }),
    ];
  }, [statusFilter, searchText, companyFilter, locationFilter, startDate, endDate, checkedByHuman]);

  return html`
    <div class="flex gap-4 h-full">
      <div
        class="flex-1 card bg-base-100 shadow-xl border border-base-300 overflow-auto ${selectedContactID
          ? 'w-2/3'
          : 'w-full'}"
      >
        <div class="card-body">
          <div class="flex justify-between items-center mb-2 sticky top-0 bg-base-100 z-10 py-4">
            <h2 class="card-title text-2xl font-bold text-secondary">Contacts</h2>

            <div class="flex items-center gap-2">
              <button class="btn btn-sm btn-outline btn-secondary" @click=${() => setShowFilters(!showFilters)}>
                ${showFilters ? 'Hide Filters' : 'Show Filters'}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-4 h-4 ml-1"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z"
                  />
                </svg>
              </button>
            </div>
          </div>

          ${showFilters
            ? html`
                <div class="bg-base-200 p-4 rounded-lg mb-4">
                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <!-- Text Search -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text">Search</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Search contacts..."
                        class="input input-bordered w-full"
                        .value=${searchText}
                        @input=${(e: InputEvent) => setSearchText((e.target as HTMLInputElement).value)}
                      />
                    </div>

                    <!-- Status Filter -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text">Status</span>
                      </label>
                      <select
                        class="select select-bordered w-full"
                        .value=${statusFilter}
                        @change=${(e: Event) => setStatusFilter((e.target as HTMLSelectElement).value as any)}
                      >
                        <option value="all">All Statuses</option>
                        <option value=${ContactStatus.ACTIVE}>Active</option>
                        <option value=${ContactStatus.INACTIVE}>Inactive</option>
                        <option value=${ContactStatus.LEAD}>Lead</option>
                        <option value=${ContactStatus.CUSTOMER}>Customer</option>
                        <option value=${ContactStatus.PROSPECT}>Prospect</option>
                      </select>
                    </div>

                    <!-- Company Filter -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text">Company</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Filter by company..."
                        class="input input-bordered w-full"
                        .value=${companyFilter}
                        @input=${(e: InputEvent) => setCompanyFilter((e.target as HTMLInputElement).value)}
                      />
                    </div>

                    <!-- Location Filter -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text">Location</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Filter by location..."
                        class="input input-bordered w-full"
                        .value=${locationFilter}
                        @input=${(e: InputEvent) => setLocationFilter((e.target as HTMLInputElement).value)}
                      />
                    </div>

                    <!-- Checked by Human -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text">Checked by Human</span>
                      </label>
                      <select
                        class="select select-bordered w-full"
                        .value=${checkedByHuman}
                        @change=${(e: InputEvent) =>
                          setCheckedByHuman((e.target as HTMLSelectElement).value as 'all' | 'yes' | 'no')}
                      >
                        <option value="all">All</option>
                        <option value="yes">Checked</option>
                        <option value="no">Not Checked</option>
                      </select>
                    </div>

                    <!-- Start Date Filter -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text">Start Date</span>
                      </label>
                      <input
                        type="date"
                        class="input input-bordered w-full"
                        .value=${startDate}
                        @input=${(e: InputEvent) => setStartDate((e.target as HTMLInputElement).value)}
                      />
                    </div>

                    <!-- End Date Filter -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text">End Date</span>
                      </label>
                      <input
                        type="date"
                        class="input input-bordered w-full"
                        .value=${endDate}
                        @input=${(e: InputEvent) => setEndDate((e.target as HTMLInputElement).value)}
                      />
                    </div>

                    <!-- Clear Filters Button -->
                    <div class="form-control md:col-span-2">
                      <label class="label opacity-0">
                        <span class="label-text">Clear</span>
                      </label>
                      <button
                        class="btn btn-ghost btn-sm text-base-content/70 hover:text-base-content flex items-center gap-1"
                        @click=${clearFilters}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          class="w-4 h-4"
                        >
                          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Clear filters
                      </button>
                    </div>
                  </div>
                </div>
              `
            : ''}

          <div class="overflow-x-auto">
            ${guard([contactsDataviewOptions], () =>
              DataTable({
                options: contactsDataviewOptions.dataViewOptions,
              }),
            )}
          </div>
        </div>
      </div>

      <!-- Detail view panel -->
      ${selectedContactID
        ? html`
            <div class="w-1/3">${ContactDetailView({ contactID: selectedContactID, onClose: closeDetailView })}</div>
          `
        : ''}
    </div>
  `;
});
